import React, { useState, useEffect, useRef } from 'react';
import {
    Box, Typography, Paper, Container, Grid,
    FormControl, InputLabel, Select, MenuItem, TextField,
    Button, Card, CardContent, CircularProgress, Chip,
    Dialog, DialogTitle, DialogContent, DialogActions,
    IconButton, Alert, Slide,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import Header from '../Header';
import { local_url, local_signage } from '../Const';

// --- Style tokens (kept consistent with the rest of the app) ---
const colors = {
    primary: '#4f46e5',
    primaryDark: '#3730a3',
    primaryGradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    success: '#10b981',
    successDark: '#059669',
    error: '#ef4444',
    errorDark: '#dc2626',
    textPrimary: '#0f172a',
    textSecondary: '#64748b',
    border: '#e2e8f0',
    backgroundGray: '#f8fafc',
    backgroundAccent: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
    backgroundCard: '#ffffff',
    white: '#ffffff',
    shadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    shadowXl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
};

const glassmorphismStyle = {
    background: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(20px) saturate(180%)',
    WebkitBackdropFilter: 'blur(20px) saturate(180%)',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    boxShadow: colors.shadowXl,
    borderRadius: '16px',
};

// --- Order Search popup — same card-grid pattern as the Customer Search modal ---
const OrderSearchModal = ({ open, onClose, orderResults, onSelectOrder, loading }) => (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '12px', ...glassmorphismStyle } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ReceiptLongIcon sx={{ mr: 1, color: colors.primary }} /> Select Order
            </Box>
            <IconButton size="small" onClick={onClose}>
                <CloseIcon fontSize="small" />
            </IconButton>
        </DialogTitle>
        <DialogContent>
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={32} sx={{ color: colors.primary }} />
                </Box>
            ) : orderResults.length === 0 ? (
                <Box sx={{
                    textAlign: 'center', py: 4, color: colors.textSecondary,
                    border: `1px dashed ${colors.border}`, borderRadius: '8px', backgroundColor: colors.backgroundGray
                }}>
                    <ReceiptLongIcon sx={{ fontSize: 36, color: colors.border, mb: 1 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>No orders found</Typography>
                    <Typography variant="caption">Try a different Order No.</Typography>
                </Box>
            ) : (
                <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                    {orderResults.map((order, index) => (
                        <Grid item xs={12} sm={6} md={4} key={order.orderNo || index}>
                            <Card
                                onClick={() => onSelectOrder(order)}
                                sx={{
                                    border: `1px solid ${colors.border}`,
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    '&:hover': { transform: 'translateY(-2px)', boxShadow: 3, borderColor: colors.primary },
                                    backgroundColor: 'rgba(255,255,255,0.85)'
                                }}
                            >
                                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                    <Typography variant="subtitle2" noWrap sx={{ display: 'flex', alignItems: 'center', fontWeight: 700 }}>
                                        <ReceiptLongIcon sx={{ mr: 0.5, fontSize: '16px', color: colors.primary }} />
                                        {order.orderNo}
                                    </Typography>
                                    <Typography variant="caption" noWrap sx={{ display: 'flex', alignItems: 'center', mt: 0.5, color: colors.textSecondary }}>
                                        <PersonOutlineIcon sx={{ mr: 0.5, fontSize: '14px' }} />
                                        {order.customerName || 'N/A'}
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                                        {order.branchId && (
                                            <Chip
                                                label={`Branch: ${order.branchId}`}
                                                size="small"
                                                sx={{ fontSize: '0.7rem', backgroundColor: '#e0e0e0' }}
                                            />
                                        )}
                                        {order.orderDate && (
                                            <Chip
                                                icon={<CalendarTodayIcon sx={{ fontSize: '12px' }} />}
                                                label={`Order Date: ${order.orderDate}`}
                                                size="small"
                                                sx={{ fontSize: '0.7rem', backgroundColor: '#e0e0e0' }}
                                            />
                                        )}
                                        {order.mobile && (
                                            <Chip
                                                label={`Mobile: ${order.mobile}`}
                                                size="small"
                                                sx={{ fontSize: '0.7rem', backgroundColor: '#e0e0e0' }}
                                            />
                                        )}
                                        {order.deliveryScheduleDate && (
                                            <Chip
                                                label={`Delivery Schedule Date: ${order.deliveryScheduleDate}`}
                                                size="small"
                                                sx={{ fontSize: '0.7rem', backgroundColor: '#e0f2fe' }}
                                            />
                                        )}
                                        {order.timeSlotName && (
                                            <Chip
                                                icon={<CalendarTodayIcon sx={{ fontSize: '12px' }} />}
                                                label={`Timeslot: ${order.timeSlotName}`}
                                                size="small"
                                                sx={{ fontSize: '0.7rem', backgroundColor: '#e0e0e0' }}
                                            />
                                        )}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}
        </DialogContent>
        <DialogActions>
            <Button onClick={onClose}>Cancel</Button>
        </DialogActions>
    </Dialog>
);

export const Deviate = () => {
    const token = localStorage.getItem('AuthorizationToken');
    const userId = localStorage.getItem('userId') || 'UnknownUser';

    // Delivery Branch filter
    const [branches, setBranches] = useState([]);
    const [selectedBranch, setSelectedBranch] = useState('ALL');
    const [loadingBranches, setLoadingBranches] = useState(false);

    // Area filter
    const [areas, setAreas] = useState([]);
    const [selectedArea, setSelectedArea] = useState('');
    const [loadingAreas, setLoadingAreas] = useState(false);

    // Order No filter (searchable, opens popup with matching orders)
    const [orderNoInput, setOrderNoInput] = useState('');
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [orderResults, setOrderResults] = useState([]);
    const [showOrderSearchModal, setShowOrderSearchModal] = useState(false);
    const [loadingOrderSearch, setLoadingOrderSearch] = useState(false);

    // Order detail (line items) shown as a table after an order is selected
    const [orderDetail, setOrderDetail] = useState(null);
    const [orderItems, setOrderItems] = useState([]);
    const [loadingOrderDetail, setLoadingOrderDetail] = useState(false);

    // Materials popup — shown when clicking an order item that has ordermatitem components
    const [materialsDialogOpen, setMaterialsDialogOpen] = useState(false);
    const [materialsDialogItem, setMaterialsDialogItem] = useState(null);

    const openMaterialsDialog = (item) => {
        setMaterialsDialogItem(item);
        setMaterialsDialogOpen(true);
    };
    const closeMaterialsDialog = () => {
        setMaterialsDialogOpen(false);
        setMaterialsDialogItem(null);
    };

    // Deviate action controls — shown below the Order Items grid
    const [deviateAction, setDeviateAction] = useState('');
    const [divertToBranch, setDivertToBranch] = useState('');
    const [deviateReason, setDeviateReason] = useState('');
    const [submittingDeviation, setSubmittingDeviation] = useState(false);

    // Reason dropdown options — fetched from GetSaleOrderCRReasons (RequestedStatus always "DV")
    const [reasons, setReasons] = useState([]);
    const [loadingReasons, setLoadingReasons] = useState(false);

    // Action dropdown options — fetched from fetchorderstatuslist (isStatus=0, isStage=0, isAction=1)
    const [actions, setActions] = useState([]);
    const [loadingActions, setLoadingActions] = useState(false);

    // Toast
    const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });
    const toastTimer = useRef();
    const showToast = (message, severity = 'info') => {
        clearTimeout(toastTimer.current);
        setToast({ open: true, message, severity });
        toastTimer.current = setTimeout(() => setToast((prev) => ({ ...prev, open: false })), 3000);
    };
    const handleCloseToast = () => {
        clearTimeout(toastTimer.current);
        setToast((prev) => ({ ...prev, open: false }));
    };

    // --- Fetch Areas — SF Area by Segment (segment is always "LAZIZ") ---
    const fetchAreas = async () => {
        setLoadingAreas(true);
        try {
            const response = await fetch(
                `${local_signage}/api/v1/sfareabysegment?SegmentCode=LAZIZ`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (!response.ok) throw new Error('Failed to fetch areas');
            const data = await response.json();
            setAreas(
                (Array.isArray(data) ? data : []).map((a) => ({
                    AreaId: a.sfAreaId ?? a.SFAreaId ?? a.areaId ?? a.AreaId,
                    AreaName: a.sfAreaName ?? a.SFAreaName ?? a.areaName ?? a.AreaName
                }))
            );
        } catch (error) {
            console.error('Error fetching areas:', error);
            setAreas([]);
            showToast('Failed to load areas.', 'error');
        } finally {
            setLoadingAreas(false);
        }
    };

    // --- Fetch Delivery Branches — Company Branch List (Segment = LAZIZ) ---
    const fetchBranches = async () => {
        setLoadingBranches(true);
        try {
            const response = await fetch(
                `${local_url}/api/Company/GetBranchList/ALL/SEGMENT/LAZIZ`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (!response.ok) throw new Error('Failed to fetch delivery branches');
            const data = await response.json();
            setBranches(
                (Array.isArray(data) ? data : []).map((b) => ({
                    BranchId: b.branchId ?? b.BranchId ?? b.branchID ?? b.BranchID,
                    BranchName: b.branchName ?? b.BranchName
                }))
            );
        } catch (error) {
            console.error('Error fetching branches:', error);
            setBranches([]);
            showToast('Failed to load delivery branches.', 'error');
        } finally {
            setLoadingBranches(false);
        }
    };

    // --- Fetch Reason dropdown options — GetSaleOrderCRReasons (RequestedStatus = "DV") ---
    const fetchReasons = async () => {
        setLoadingReasons(true);
        try {
            const response = await fetch(
                `${local_url}/api/SaleOrderList/GetSaleOrderCRReasons/DV`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (!response.ok) throw new Error('Failed to fetch reasons');
            const data = await response.json();
            setReasons(
                (Array.isArray(data) ? data : []).map((r) => ({
                    ReasonId: r.requestReasonCode ?? r.RequestReasonCode ?? r.ReasonId ?? r.reasonId ?? r.ReasonCode ?? r.reasonCode ?? r.Id ?? r.id,
                    ReasonName: r.requestReasonDesc ?? r.RequestReasonDesc ?? r.ReasonName ?? r.reasonName ?? r.Description ?? r.description ?? r.Reason ?? r.reason
                }))
            );
        } catch (error) {
            console.error('Error fetching reasons:', error);
            setReasons([]);
            showToast('Failed to load reasons.', 'error');
        } finally {
            setLoadingReasons(false);
        }
    };

    // --- Fetch Action dropdown options — fetchorderstatuslist (isStatus=0, isStage=0, isAction=1) ---
    const fetchActions = async () => {
        setLoadingActions(true);
        try {
            const response = await fetch(
                `${local_signage}/api/v1/fetchorderstatuslist?isStatus=0&isStage=0&isAction=1`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (!response.ok) throw new Error('Failed to fetch actions');
            const data = await response.json();
            setActions(
                (Array.isArray(data) ? data : [])
                    .filter((a) => a.isAction ?? true)
                    .map((a) => ({
                        ActionCode: a.DeliveryStatusCode ?? a.deliveryStatusCode,
                        ActionName: a.DeliveryStatusName ?? a.deliveryStatusName
                    }))
            );
        } catch (error) {
            console.error('Error fetching actions:', error);
            setActions([]);
            showToast('Failed to load actions.', 'error');
        } finally {
            setLoadingActions(false);
        }
    };

    useEffect(() => {
        if (token) {
            fetchAreas();
            fetchBranches();
            fetchReasons();
            fetchActions();
        } else {
            showToast('No authorization token found. Please login first.', 'error');
        }
    }, [token]);

    const handleBranchChange = (branchId) => {
        setSelectedBranch(branchId);
    };

    // --- Date helpers: today -10 days / today +10 days, formatted YYYYMMDD ---
    const formatYyyymmdd = (date) => {
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        return `${yyyy}${mm}${dd}`;
    };
    const getOrderSearchDateRange = () => {
        const today = new Date();
        const fromDate = new Date(today);
        fromDate.setDate(today.getDate() - 10);
        const toDate = new Date(today);
        toDate.setDate(today.getDate() + 10);
        return { fromDate: formatYyyymmdd(fromDate), toDate: formatYyyymmdd(toDate) };
    };

    // --- Format helpers for API date strings like "2026-05-11T12:25:08.000Z" ---
    const formatApiDate = (value) => {
        if (!value) return '';
        const datePart = String(value).split('T')[0];
        const [yyyy, mm, dd] = datePart.split('-');
        if (!yyyy || !mm || !dd) return datePart;
        return `${dd}-${mm}-${yyyy}`;
    };

    // --- Order search (searchable filter -> popup with results) ---
    // Segment is always "LAZIZ", OrderStatus is always "PN", and the date window is
    // always today-10 to today+10. Area and Branch come from the dropdowns above.
    // SearchText is a REQUIRED, non-empty query param for this API — both omitting
    // it and sending an empty string return a 400 "Missing required query
    // parameters". When the Order No field is blank, send "ALL" instead.
    const handleOrderSearch = async () => {
        if (!selectedArea) {
            showToast('Please select an Area first.', 'warning');
            return;
        }
        setLoadingOrderSearch(true);
        setShowOrderSearchModal(true);
        try {
            const { fromDate, toDate } = getOrderSearchDateRange();
            const trimmedSearch = orderNoInput.trim();

            const params = new URLSearchParams({
                Segment: 'LAZIZ',
                AreaId: selectedArea,
                BranchId: selectedBranch,
                OrderStatus: 'PN',
                FromDate: fromDate,
                ToDate: toDate,
                SearchText: trimmedSearch || 'ALL'
            });

            const response = await fetch(
                `${local_signage}/api/v1/fetchsaleordersearch?${params.toString()}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (!response.ok) throw new Error('Order search failed');
            const data = await response.json();

            // API can return a bare array, an object wrapping an array (data/Data/orders),
            // or — for a single match — a single order object. Normalize to an array.
            const results = Array.isArray(data)
                ? data
                : Array.isArray(data?.data)
                    ? data.data
                    : Array.isArray(data?.Data)
                        ? data.Data
                        : Array.isArray(data?.orders)
                            ? data.orders
                            : (data && typeof data === 'object' && Object.keys(data).length > 0)
                                ? [data]
                                : [];

            const selectedAreaName = areas.find((a) => a.AreaId === selectedArea)?.AreaName || '';

            const mapped = results.map((o) => ({
                orderNo: o.SaleOrderID ?? o.saleOrderId ?? o.SaleOrderId ?? o.OrderNo ?? o.orderNo,
                orderDate: formatApiDate(o.OrderDate ?? o.orderDate),
                branchId: o.DelBranchID ?? o.delBranchId ?? o.DelBranchId ?? o.BranchId ?? o.branchId,
                customerName: o.CustomerName ?? o.customerName,
                customerAddress: o.CustomerAddress ?? o.customerAddress,
                pincode: o.PINCODE ?? o.Pincode ?? o.pincode,
                mobile: o.Mobile ?? o.mobile,
                timeSlotName: o.TimeSlotName ?? o.timeSlotName,
                deliveryScheduleDate: formatApiDate(o.DeliveryScheduleDate ?? o.deliveryScheduleDate),
                orderStatusId: o.OrderStatusId ?? o.orderStatusId,
                // API doesn't return a readable area name — use the one already picked in the dropdown.
                areaName: selectedAreaName
            }));

            setOrderResults(mapped);
        } catch (error) {
            console.error('Error searching orders:', error);
            setOrderResults([]);
            showToast('Failed to search orders.', 'error');
        } finally {
            setLoadingOrderSearch(false);
        }
    };

    const handleOrderKeyPress = (event) => {
        if (event.key === 'Enter') handleOrderSearch();
    };

    // --- Order detail — line items for the selected order ---
    const fetchOrderDetail = async (saleOrderId) => {
        if (!saleOrderId) return;
        setLoadingOrderDetail(true);
        setOrderDetail(null);
        setOrderItems([]);
        closeMaterialsDialog();
        try {
            const response = await fetch(
                `${local_signage}/api/v1/getjsonsaleorderdetail?SaleOrderId=${encodeURIComponent(saleOrderId)}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (!response.ok) throw new Error('Failed to fetch order detail');
            const data = await response.json();
            console.log('Order detail API response:', data);

            // Be liberal about the response shape — try every wrapper key variant seen
            // in practice, and fall back to treating `data` itself as the header object
            // if it already carries the line-items array directly.
            const saleOrderArray =
                (Array.isArray(data?.SaleOrder) && data.SaleOrder) ||
                (Array.isArray(data?.saleOrder) && data.saleOrder) ||
                (Array.isArray(data?.SaleOrders) && data.SaleOrders) ||
                (Array.isArray(data?.data?.SaleOrder) && data.data.SaleOrder) ||
                (Array.isArray(data) && data) ||
                (data && typeof data === 'object' ? [data] : []);

            const header = saleOrderArray[0] || null;

            const items =
                (header && (
                    header.orderwebitem ??
                    header.OrderWebItem ??
                    header.orderWebItem ??
                    header.OrderWebItems ??
                    header.Orderwebitem ??
                    header.items
                )) || [];

            setOrderDetail(header);
            setOrderItems(Array.isArray(items) ? items : []);

            if (!header || !Array.isArray(items) || items.length === 0) {
                showToast('No order details found.', 'info');
            }
        } catch (error) {
            console.error('Error fetching order detail:', error);
            setOrderDetail(null);
            setOrderItems([]);
            showToast('Failed to load order details.', 'error');
        } finally {
            setLoadingOrderDetail(false);
        }
    };

    const handleSelectOrder = (order) => {
        setSelectedOrder(order);
        setOrderNoInput(order.orderNo);
        setShowOrderSearchModal(false);
        showToast(`Order ${order.orderNo} selected.`, 'success');
        fetchOrderDetail(order.orderNo);
        setDeviateAction('');
        setDivertToBranch('');
        setDeviateReason('');
    };

    const handleClearOrder = () => {
        setSelectedOrder(null);
        setOrderNoInput('');
        setOrderResults([]);
        setOrderDetail(null);
        setOrderItems([]);
        closeMaterialsDialog();
        setDeviateAction('');
        setDivertToBranch('');
        setDeviateReason('');
    };

    // --- Divert to Branch selection — block picking the order's own current delivery branch ---
    const isSameAsCurrentBranch = (branchId) => {
        const selectedBranchName = (branches.find((b) => b.BranchId === branchId)?.BranchName || '').trim().toLowerCase();
        const currentBranchName = (orderDetail?.DelBranchName || '').trim().toLowerCase();
        return !!selectedBranchName && !!currentBranchName && selectedBranchName === currentBranchName;
    };

    const handleDivertBranchChange = (branchId) => {
        if (isSameAsCurrentBranch(branchId)) {
            showToast('You cannot select the current delivery branch. Please select another branch.', 'warning');
            return;
        }
        setDivertToBranch(branchId);
    };

    // --- Submit deviation — Action + Divert To Branch + Reason for the selected order ---
    const handleSubmitDeviation = async () => {
        if (!deviateAction) {
            showToast('Please select an Action.', 'warning');
            return;
        }
        if (deviateAction === 'DV' && !divertToBranch) {
            showToast('Please select a branch to divert to.', 'warning');
            return;
        }
        if (deviateAction === 'DV' && isSameAsCurrentBranch(divertToBranch)) {
            showToast('You cannot select the current delivery branch. Please select another branch.', 'warning');
            return;
        }
        if (!deviateReason) {
            showToast('Please select a Reason.', 'warning');
            return;
        }
        setSubmittingDeviation(true);
        try {
            const response = await fetch(
                `${local_signage}/api/v1/updatesaleorderstatuschange`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        SaleOrderId: selectedOrder?.orderNo,
                        NewStatusID: deviateAction,
                        ToBranchId: divertToBranch,
                        ReasonCode: deviateReason,
                        CreatedUserId: userId
                    })
                }
            );

            if (!response.ok) {
                let detail = '';
                try {
                    const errBody = await response.json();
                    detail = errBody?.error || errBody?.message || errBody?.description || errBody?.Description || '';
                } catch {
                    detail = await response.text();
                }
                throw new Error(detail || 'Failed to submit deviation');
            }

            // Real response shape: { success, message, data: [{ Result, Description }] }
            // Result: 1 = success ("Order deviated"), Result: 0 = failure ("invalid Request code").
            let body = null;
            try {
                body = await response.json();
            } catch {
                body = null;
            }

            const resultArray =
                Array.isArray(body?.data) ? body.data :
                Array.isArray(body?.Data) ? body.Data :
                Array.isArray(body) ? body :
                (body ? [body] : []);

            const resultRow = resultArray[0] || null;
            const result = resultRow?.Result ?? resultRow?.result;
            const description = resultRow?.Description ?? resultRow?.description;
            const topLevelMessage = body?.message ?? body?.Message;
            const topLevelSuccess = body?.success ?? body?.Success;

            // A row with Result present and not equal to 1 is a business-level failure.
            if (resultRow && result !== undefined && Number(result) !== 1) {
                showToast(description || topLevelMessage || 'Invalid request. Deviation not submitted.', 'error');
                return;
            }
            // No result row at all, but the wrapper itself flags failure.
            if (!resultRow && topLevelSuccess === false) {
                showToast(topLevelMessage || 'Failed to submit deviation.', 'error');
                return;
            }

            showToast(description || topLevelMessage || 'Deviation submitted successfully!', 'success');

            // Reset the action controls and refresh the order detail so the
            // updated status/delivery status is reflected on screen.
            setDeviateAction('');
            setDivertToBranch('');
            setDeviateReason('');
            if (selectedOrder?.orderNo) {
                fetchOrderDetail(selectedOrder.orderNo);
            }
        } catch (error) {
            console.error('Error submitting deviation:', error);
            showToast(error.message || 'Failed to submit deviation.', 'error');
        } finally {
            setSubmittingDeviation(false);
        }
    };

    return (
        <>
            <Header />
            <Box sx={{
                width: '100vw',
                minHeight: '90vh',
                background: colors.backgroundAccent,
                p: 0.5,
            }}>
                <Container maxWidth="xl" sx={{ p: '0 !important' }}>
                    <Paper elevation={0} sx={{
                        ...glassmorphismStyle,
                        p: 2,
                        background: colors.backgroundCard,
                        border: `1px solid ${colors.border}`,
                    }}>
                        {/* Header */}
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, pb: 1.5, borderBottom: `1px solid ${colors.border}` }}>
                            <ReceiptLongIcon sx={{ mr: 1.5, color: colors.primary, fontSize: '28px' }} />
                            <Typography variant="h6" sx={{ fontSize: '1.2rem', fontWeight: 700, color: colors.textPrimary }}>
                                Manage Orders
                            </Typography>
                        </Box>

                        {/* Filters */}
                        <Grid container spacing={1.5} alignItems="center">
                            {/* Area — fetched from sfareabysegment (SegmentCode=LAZIZ) */}
                            <Grid item xs={12} sm={4} md={3}>
                                <FormControl fullWidth size="small">
                                    <InputLabel id="area-label" sx={{ fontSize: '0.8rem' }}>Area</InputLabel>
                                    <Select
                                        labelId="area-label"
                                        value={selectedArea}
                                        label="Area"
                                        onChange={(e) => setSelectedArea(e.target.value)}
                                        disabled={loadingAreas}
                                        sx={{
                                            height: '36px',
                                            fontSize: '0.85rem',
                                            '& .MuiSelect-select': { padding: '8px 10px', fontSize: '0.85rem' }
                                        }}
                                    >
                                        {areas.map((a) => (
                                            <MenuItem key={a.AreaId} value={a.AreaId} sx={{ fontSize: '0.85rem' }}>
                                                {a.AreaName}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {loadingAreas && <CircularProgress size={16} sx={{ mt: 0.5 }} />}
                                </FormControl>
                            </Grid>

                            {/* Delivery Branch — fetched from Company/GetBranchList (Segment=LAZIZ) */}
                            <Grid item xs={12} sm={4} md={3}>
                                <FormControl fullWidth size="small">
                                    <InputLabel id="delivery-branch-label" sx={{ fontSize: '0.8rem' }}>Delivery Branch</InputLabel>
                                    <Select
                                        labelId="delivery-branch-label"
                                        value={selectedBranch}
                                        label="Delivery Branch"
                                        onChange={(e) => handleBranchChange(e.target.value)}
                                        disabled={loadingBranches}
                                        sx={{
                                            height: '36px',
                                            fontSize: '0.85rem',
                                            '& .MuiSelect-select': { padding: '8px 10px', fontSize: '0.85rem' }
                                        }}
                                    >
                                        <MenuItem value="ALL" sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                                            ALL
                                        </MenuItem>
                                        {branches.map((b) => (
                                            <MenuItem key={b.BranchId} value={b.BranchId} sx={{ fontSize: '0.85rem' }}>
                                                {b.BranchName}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {loadingBranches && <CircularProgress size={16} sx={{ mt: 0.5 }} />}
                                </FormControl>
                            </Grid>

                            {/* Order No — searchable, opens popup */}
                            <Grid item xs={12} sm={4} md={6}>
                                <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start' }}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Order No"
                                        placeholder="Search by Order ID"
                                        value={orderNoInput}
                                        onChange={(e) => setOrderNoInput(e.target.value)}
                                        onKeyPress={handleOrderKeyPress}
                                        InputProps={{
                                            endAdornment: selectedOrder ? (
                                                <IconButton size="small" onClick={handleClearOrder}>
                                                    <CloseIcon sx={{ fontSize: '16px' }} />
                                                </IconButton>
                                            ) : null
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': { height: '36px' },
                                            '& .MuiInputBase-input': { padding: '8px 10px', fontSize: '0.85rem' },
                                            '& .MuiInputLabel-root': { fontSize: '0.8rem' }
                                        }}
                                    />
                                    <Button
                                        variant="contained"
                                        onClick={handleOrderSearch}
                                        disabled={loadingOrderSearch}
                                        sx={{
                                            minWidth: '36px',
                                            width: '36px',
                                            height: '36px',
                                            p: 0,
                                            flexShrink: 0,
                                            backgroundColor: colors.primary,
                                            '&:hover': { backgroundColor: colors.primaryDark }
                                        }}
                                    >
                                        {loadingOrderSearch ? <CircularProgress size={16} color="inherit" /> : <SearchIcon sx={{ fontSize: '18px' }} />}
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>

                        {/* Order detail — header info + item grid for the selected order */}
                        {selectedOrder && (
                            <Box sx={{ mt: 2 }}>
                                {loadingOrderDetail ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                        <CircularProgress size={28} sx={{ color: colors.primary }} />
                                    </Box>
                                ) : orderDetail ? (
                                    <>
                                        {/* Order header — shown outside/above the item grid */}
                                        <Box sx={{
                                            mb: 2, p: 1.5, borderRadius: '10px',
                                            border: `1px solid ${colors.border}`, backgroundColor: colors.backgroundGray,
                                            display: 'flex', flexWrap: 'wrap', gap: 4
                                        }}>
                                            {/* Left column — order identity, stacked top to bottom */}
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                                <Typography variant="body2" sx={{ color: colors.textPrimary }}>
                                                    <strong>Sale Order ID:</strong> {orderDetail.SaleOrderid ?? orderDetail.SaleOrderId ?? orderDetail.SaleOrderID ?? '-'}
                                                </Typography>
                                                {orderDetail.OrderDate && (
                                                    <Typography variant="body2" sx={{ color: colors.textPrimary }}>
                                                        <strong>Order Date:</strong> {formatApiDate(orderDetail.OrderDate)}
                                                    </Typography>
                                                )}
                                                {orderDetail.OrderStatusId && (
                                                    <Typography variant="body2" sx={{ color: colors.textPrimary }}>
                                                        <strong>Order Status:</strong> {orderDetail.OrderStatusId}
                                                    </Typography>
                                                )}
                                                {orderDetail.DeliveryStatusName && (
                                                    <Typography variant="body2" sx={{ color: colors.successDark, fontWeight: 600 }}>
                                                        <strong>Delivery Status:</strong> {orderDetail.DeliveryStatusName}
                                                    </Typography>
                                                )}
                                            </Box>

                                            {/* Right column — customer / delivery details, stacked top to bottom */}
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                                {orderDetail.CustomerName && (
                                                    <Typography variant="body2" sx={{ color: colors.textPrimary }}>
                                                        <strong>Customer Name:</strong> {orderDetail.CustomerName}
                                                    </Typography>
                                                )}
                                                {orderDetail.Mobile && (
                                                    <Typography variant="body2" sx={{ color: colors.textPrimary }}>
                                                        <strong>Mobile:</strong> {orderDetail.Mobile}
                                                    </Typography>
                                                )}
                                                {orderDetail.DelBranchName && (
                                                    <Typography variant="body2" sx={{ color: colors.textPrimary }}>
                                                        <strong>Branch:</strong> {orderDetail.DelBranchName}
                                                    </Typography>
                                                )}
                                                {orderDetail.DeliveryScheduleDate && (
                                                    <Typography variant="body2" sx={{ color: colors.textPrimary }}>
                                                        <strong>Delivery Schedule Date:</strong> {formatApiDate(orderDetail.DeliveryScheduleDate)}
                                                    </Typography>
                                                )}
                                                {(orderDetail.TimeSlot || orderDetail.TimeSlotId) && (
                                                    <Typography variant="body2" sx={{ color: colors.textPrimary }}>
                                                        <strong>Timeslot:</strong> {orderDetail.TimeSlot ?? orderDetail.TimeSlotId}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>

                                        {/* Order items — grid, child of the order header above */}
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: colors.textPrimary }}>
                                            Order Items
                                        </Typography>

                                        {orderItems.length > 0 ? (
                                            <TableContainer component={Paper} variant="outlined" sx={{ borderColor: colors.border }}>
                                                <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                                                    <colgroup>
                                                        <col style={{ width: '4%' }} />
                                                        <col style={{ width: '16%' }} />
                                                        <col style={{ width: '38%' }} />
                                                        <col style={{ width: '12%' }} />
                                                        <col style={{ width: '14%' }} />
                                                        <col style={{ width: '16%' }} />
                                                    </colgroup>
                                                    <TableHead>
                                                        <TableRow sx={{ '& .MuiTableCell-root': { fontWeight: 700, backgroundColor: colors.backgroundGray } }}>
                                                            <TableCell />
                                                            <TableCell>Item ID</TableCell>
                                                            <TableCell>Item Name</TableCell>
                                                            <TableCell align="right">Qty</TableCell>
                                                            <TableCell align="right">Rate</TableCell>
                                                            <TableCell align="right">Total Amount</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {orderItems.map((item, itemIndex) => {
                                                            const materials = Array.isArray(item.ordermatitem) ? item.ordermatitem : [];
                                                            const rowKey = item.WebItemId ?? itemIndex;
                                                            const hasMaterials = materials.length > 0;
                                                            return (
                                                                <TableRow
                                                                    key={rowKey}
                                                                    hover
                                                                    onClick={() => hasMaterials && openMaterialsDialog(item)}
                                                                    sx={{ cursor: hasMaterials ? 'pointer' : 'default' }}
                                                                >
                                                                    <TableCell>
                                                                        {hasMaterials && (
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={(e) => { e.stopPropagation(); openMaterialsDialog(item); }}
                                                                            >
                                                                                <KeyboardArrowRightIcon fontSize="small" />
                                                                            </IconButton>
                                                                        )}
                                                                    </TableCell>
                                                                    <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.WebItemId ?? '-'}</TableCell>
                                                                    <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.ItemName ?? '-'}</TableCell>
                                                                    <TableCell align="right">{item.WebQty ?? '-'}</TableCell>
                                                                    <TableCell align="right">{item.Rate ?? '-'}</TableCell>
                                                                    <TableCell align="right">{item.TotalAmount ?? '-'}</TableCell>
                                                                </TableRow>
                                                            );
                                                        })}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        ) : (
                                            <Box sx={{
                                                textAlign: 'center', py: 3, color: colors.textSecondary,
                                                border: `1px dashed ${colors.border}`, borderRadius: '8px', backgroundColor: colors.backgroundGray
                                            }}>
                                                <Typography variant="body2">No item details found for this order.</Typography>
                                            </Box>
                                        )}

                                        {/* Actions section */}
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 3, mb: 1, color: colors.textPrimary }}>
                                            Actions
                                        </Typography>
                                        <Box sx={{
                                            display: 'flex', justifyContent: 'flex-start',
                                            alignItems: 'flex-start', flexWrap: 'wrap', gap: 1.5
                                        }}>
                                            <FormControl size="small" sx={{ minWidth: 200 }}>
                                                <InputLabel id="deviate-action-label" sx={{ fontSize: '0.8rem' }}>Action</InputLabel>
                                                <Select
                                                    labelId="deviate-action-label"
                                                    value={deviateAction}
                                                    label="Action"
                                                    onChange={(e) => setDeviateAction(e.target.value)}
                                                    disabled={loadingActions}
                                                    sx={{
                                                        height: '36px',
                                                        fontSize: '0.85rem',
                                                        '& .MuiSelect-select': { padding: '8px 10px', fontSize: '0.85rem' }
                                                    }}
                                                >
                                                    {actions.map((a) => (
                                                        <MenuItem key={a.ActionCode} value={a.ActionCode} sx={{ fontSize: '0.85rem' }}>
                                                            {a.ActionName}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                                {loadingActions && <CircularProgress size={16} sx={{ mt: 0.5 }} />}
                                            </FormControl>

                                            <FormControl size="small" sx={{ minWidth: 220 }}>
                                                <InputLabel id="divert-to-branch-label" sx={{ fontSize: '0.8rem' }}>Divert to Branch</InputLabel>
                                                <Select
                                                    labelId="divert-to-branch-label"
                                                    value={divertToBranch}
                                                    label="Divert to Branch"
                                                    onChange={(e) => handleDivertBranchChange(e.target.value)}
                                                    disabled={loadingBranches}
                                                    sx={{
                                                        height: '36px',
                                                        fontSize: '0.85rem',
                                                        '& .MuiSelect-select': { padding: '8px 10px', fontSize: '0.85rem' }
                                                    }}
                                                >
                                                    {branches.map((b) => (
                                                        <MenuItem key={b.BranchId} value={b.BranchId} sx={{ fontSize: '0.85rem' }}>
                                                            {b.BranchName}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                                {loadingBranches && <CircularProgress size={16} sx={{ mt: 0.5 }} />}
                                            </FormControl>

                                            <FormControl size="small" sx={{ minWidth: 260 }}>
                                                <InputLabel id="deviate-reason-label" sx={{ fontSize: '0.8rem' }}>Reason</InputLabel>
                                                <Select
                                                    labelId="deviate-reason-label"
                                                    value={deviateReason}
                                                    label="Reason"
                                                    onChange={(e) => setDeviateReason(e.target.value)}
                                                    disabled={loadingReasons}
                                                    sx={{
                                                        height: '36px',
                                                        fontSize: '0.85rem',
                                                        '& .MuiSelect-select': { padding: '8px 10px', fontSize: '0.85rem' }
                                                    }}
                                                >
                                                    {reasons.map((r) => (
                                                        <MenuItem key={r.ReasonId} value={r.ReasonId} sx={{ fontSize: '0.85rem' }}>
                                                            {r.ReasonName}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                                {loadingReasons && <CircularProgress size={16} sx={{ mt: 0.5 }} />}
                                            </FormControl>

                                            <Button
                                                variant="contained"
                                                onClick={handleSubmitDeviation}
                                                disabled={submittingDeviation}
                                                sx={{
                                                    height: '36px',
                                                    background: colors.primaryGradient,
                                                    color: colors.white,
                                                    borderRadius: '8px',
                                                    fontWeight: 600,
                                                    textTransform: 'none',
                                                    fontSize: '0.85rem',
                                                    px: 3
                                                }}
                                            >
                                                {submittingDeviation ? <CircularProgress size={16} color="inherit" /> : 'Submit'}
                                            </Button>
                                        </Box>
                                    </>
                                ) : (
                                    <Box sx={{
                                        textAlign: 'center', py: 3, color: colors.textSecondary,
                                        border: `1px dashed ${colors.border}`, borderRadius: '8px', backgroundColor: colors.backgroundGray
                                    }}>
                                        <Typography variant="body2">No item details found for this order.</Typography>
                                    </Box>
                                )}
                            </Box>
                        )}

                    </Paper>
                </Container>

                {/* Order search popup */}
                <OrderSearchModal
                    open={showOrderSearchModal}
                    onClose={() => setShowOrderSearchModal(false)}
                    orderResults={orderResults}
                    onSelectOrder={handleSelectOrder}
                    loading={loadingOrderSearch}
                />

                {/* Materials popup — shows the ordermatitem components for the clicked order item */}
                <Dialog
                    open={materialsDialogOpen}
                    onClose={closeMaterialsDialog}
                    maxWidth="lg"
                    fullWidth
                    PaperProps={{ sx: { borderRadius: '12px', ...glassmorphismStyle } }}
                >
                    <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                {materialsDialogItem?.ItemName ?? 'Item Components'}
                            </Typography>
                            {materialsDialogItem?.WebItemId && (
                                <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                                    Item ID: {materialsDialogItem.WebItemId}
                                </Typography>
                            )}
                        </Box>
                        <IconButton size="small" onClick={closeMaterialsDialog}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent>
                        <TableContainer component={Paper} variant="outlined" sx={{ borderColor: colors.border, mt: 1 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ '& .MuiTableCell-root': { fontWeight: 700, backgroundColor: colors.backgroundGray } }}>
                                        <TableCell>Mat Item ID</TableCell>
                                        <TableCell>Mat Item Name</TableCell>
                                        <TableCell>Mat Item Family Name</TableCell>
                                        <TableCell align="right">Mat Qty</TableCell>
                                        <TableCell align="right">Stock Qty</TableCell>
                                        <TableCell align="right" sx={{ minWidth: 180 }}>Possible Units</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(Array.isArray(materialsDialogItem?.ordermatitem) ? materialsDialogItem.ordermatitem : []).map((mat, matIndex) => {
                                        const stockQty = Number(mat.StockQty);
                                        const itemUnitQty = Number(mat.ItemUnitQty);
                                        const matQty = Number(mat.MatQty);
                                        const possibleUnits = (!isNaN(stockQty) && !isNaN(itemUnitQty) && itemUnitQty > 0)
                                            ? Math.floor(stockQty / itemUnitQty)
                                            : null;
                                        const shortfall = (possibleUnits !== null && !isNaN(matQty))
                                            ? matQty - possibleUnits
                                            : null;
                                        return (
                                            <TableRow key={mat.MatItemId ?? matIndex} hover>
                                                <TableCell>{mat.MatItemId ?? '-'}</TableCell>
                                                <TableCell>{mat.MatItemName ?? '-'}</TableCell>
                                                <TableCell>{mat.MatItemFamilyName ?? '-'}</TableCell>
                                                <TableCell align="right">{mat.MatQty ?? '-'}</TableCell>
                                                <TableCell align="right">{mat.StockQty ?? '-'}</TableCell>
                                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                                    {possibleUnits !== null ? (
                                                        <Box component="span">
                                                            {possibleUnits}
                                                            {shortfall !== null && shortfall > 0 && (
                                                                <Box
                                                                    component="span"
                                                                    sx={{ color: colors.error, fontWeight: 600, ml: 0.5 }}
                                                                >
                                                                    (short by {shortfall} units)
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    ) : '-'}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={closeMaterialsDialog}>Close</Button>
                    </DialogActions>
                </Dialog>

                {/* Toast */}
                <Slide direction="up" in={toast.open} mountOnEnter unmountOnExit>
                    <Alert
                        severity={toast.severity}
                        sx={{
                            position: 'fixed',
                            bottom: 24,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            minWidth: 300,
                            zIndex: 1400,
                            borderRadius: '8px',
                            alignItems: 'center',
                            ...glassmorphismStyle,
                        }}
                        action={
                            <IconButton size="small" color="inherit" onClick={handleCloseToast}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        }
                    >
                        {toast.message}
                    </Alert>
                </Slide>
            </Box>
        </>
    );
};

export default Deviate;
